/**
 * Advanced Physics nodes — zones, torque, overlap queries, constraints.
 */
import type { NodeBehaviour } from '@engine/redprint';
import { flowIn, flowOut, input, output } from '@engine/redprint';

// ============================================================================
// ANGULAR DYNAMICS
// ============================================================================

export const SetAngularVelocity: NodeBehaviour = {
  id: 'physics.setAngularVelocity',
  name: 'Set Angular Velocity',
  category: 'Physics',
  color: '#4ecdc4',
  icon: 'RotateCw',
  description: 'Sets the angular velocity (spin) of a rigidbody.',
  keywords: ['spin', 'rotation', 'torque'],
  inputs: [
    flowIn(),
    input('entity', 'Entity', 'entity', 0),
    input('omega', 'Angular Velocity', 'f32', 0),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:setAngularVelocity', [ctx.resolveInput('entity'), ctx.resolveInput('omega')], 'void', true);
  },
  execute(ctx) {
    const eid = ctx.getInput<number>('entity') || ctx.entityId;
    ctx.bridge.call('bridge:setAngularVelocity', eid, ctx.getInput<number>('omega'));
  },
};

export const GetAngularVelocity: NodeBehaviour = {
  id: 'physics.getAngularVelocity',
  name: 'Get Angular Velocity',
  category: 'Physics',
  isPure: true,
  color: '#4ecdc4',
  icon: 'RotateCw',
  description: 'Gets the angular velocity of a rigidbody.',
  keywords: ['spin', 'rotation'],
  inputs: [
    input('entity', 'Entity', 'entity', 0),
  ],
  outputs: [
    output('omega', 'Angular Velocity', 'f32'),
  ],
  compile(ctx) {
    return ctx.emit('bridge:getAngularVelocity', [ctx.resolveInput('entity')], 'f32');
  },
  execute(ctx) {
    const eid = ctx.getInput<number>('entity') || ctx.entityId;
    ctx.setOutput('omega', ctx.bridge.call('bridge:getAngularVelocity', eid) as number ?? 0);
  },
};

export const AddTorque: NodeBehaviour = {
  id: 'physics.addTorque',
  name: 'Add Torque',
  category: 'Physics',
  color: '#4ecdc4',
  icon: 'RotateCw',
  description: 'Applies rotational force to a rigidbody.',
  keywords: ['spin', 'rotate', 'twist'],
  inputs: [
    flowIn(),
    input('entity', 'Entity', 'entity', 0),
    input('torque', 'Torque', 'f32', 0),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:addTorque', [ctx.resolveInput('entity'), ctx.resolveInput('torque')], 'void', true);
  },
  execute(ctx) {
    const eid = ctx.getInput<number>('entity') || ctx.entityId;
    ctx.bridge.call('bridge:addTorque', eid, ctx.getInput<number>('torque'));
  },
};

// ============================================================================
// FORCE AT POINT
// ============================================================================

export const AddForceAtPoint: NodeBehaviour = {
  id: 'physics.addForceAtPoint',
  name: 'Add Force At Point',
  category: 'Physics',
  color: '#4ecdc4',
  icon: 'Crosshair',
  description: 'Applies force at a specific world position, creating torque.',
  keywords: ['push', 'offset', 'moment'],
  inputs: [
    flowIn(),
    input('entity', 'Entity', 'entity', 0),
    input('forceX', 'Force X', 'f32', 0),
    input('forceY', 'Force Y', 'f32', 0),
    input('pointX', 'Point X', 'f32', 0),
    input('pointY', 'Point Y', 'f32', 0),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:addForceAtPoint', [
      ctx.resolveInput('entity'),
      ctx.resolveInput('forceX'), ctx.resolveInput('forceY'),
      ctx.resolveInput('pointX'), ctx.resolveInput('pointY'),
    ], 'void', true);
  },
  execute(ctx) {
    const eid = ctx.getInput<number>('entity') || ctx.entityId;
    ctx.bridge.call('bridge:addForceAtPoint', eid, ctx.getInput<number>('forceX'), ctx.getInput<number>('forceY'), ctx.getInput<number>('pointX'), ctx.getInput<number>('pointY'));
  },
};

// ============================================================================
// OVERLAP QUERIES
// ============================================================================

export const OverlapCircle: NodeBehaviour = {
  id: 'physics.overlapCircle',
  name: 'Overlap Circle',
  category: 'Physics',
  isPure: true,
  color: '#4ecdc4',
  icon: 'Circle',
  description: 'Returns the first entity overlapping with a circle.',
  keywords: ['collide', 'detect', 'intersect', 'area'],
  inputs: [
    input('x', 'Center X', 'f32', 0),
    input('y', 'Center Y', 'f32', 0),
    input('radius', 'Radius', 'f32', 1),
    input('layer', 'Layer Mask', 'i32', -1),
  ],
  outputs: [
    output('hit', 'Did Hit', 'bool'),
    output('entity', 'Entity', 'entity'),
  ],
  compile(ctx) {
    return ctx.emit('bridge:overlapCircle', [
      ctx.resolveInput('x'), ctx.resolveInput('y'),
      ctx.resolveInput('radius'), ctx.resolveInput('layer'),
    ], 'overlapResult');
  },
  execute(ctx) {
    const r = ctx.bridge.call('bridge:overlapCircle', ctx.getInput<number>('x'), ctx.getInput<number>('y'), ctx.getInput<number>('radius'), ctx.getInput<number>('layer')) as { hit: boolean; entity: number } | null;
    ctx.setOutput('hit', r?.hit ?? false);
    ctx.setOutput('entity', r?.entity ?? -1);
  },
};

export const OverlapBox: NodeBehaviour = {
  id: 'physics.overlapBox',
  name: 'Overlap Box',
  category: 'Physics',
  isPure: true,
  color: '#4ecdc4',
  icon: 'Square',
  description: 'Returns the first entity overlapping with an axis-aligned box.',
  keywords: ['collide', 'detect', 'intersect', 'area', 'aabb'],
  inputs: [
    input('x', 'Center X', 'f32', 0),
    input('y', 'Center Y', 'f32', 0),
    input('width', 'Width', 'f32', 1),
    input('height', 'Height', 'f32', 1),
    input('layer', 'Layer Mask', 'i32', -1),
  ],
  outputs: [
    output('hit', 'Did Hit', 'bool'),
    output('entity', 'Entity', 'entity'),
  ],
  compile(ctx) {
    return ctx.emit('bridge:overlapBox', [
      ctx.resolveInput('x'), ctx.resolveInput('y'),
      ctx.resolveInput('width'), ctx.resolveInput('height'),
      ctx.resolveInput('layer'),
    ], 'overlapResult');
  },
  execute(ctx) {
    const r = ctx.bridge.call('bridge:overlapBox', ctx.getInput<number>('x'), ctx.getInput<number>('y'), ctx.getInput<number>('width'), ctx.getInput<number>('height'), ctx.getInput<number>('layer')) as { hit: boolean; entity: number } | null;
    ctx.setOutput('hit', r?.hit ?? false);
    ctx.setOutput('entity', r?.entity ?? -1);
  },
};

export const OverlapPoint: NodeBehaviour = {
  id: 'physics.overlapPoint',
  name: 'Overlap Point',
  category: 'Physics',
  isPure: true,
  color: '#4ecdc4',
  icon: 'MapPin',
  description: 'Returns the entity at a specific point.',
  keywords: ['pick', 'select', 'collide'],
  inputs: [
    input('x', 'X', 'f32', 0),
    input('y', 'Y', 'f32', 0),
    input('layer', 'Layer Mask', 'i32', -1),
  ],
  outputs: [
    output('hit', 'Did Hit', 'bool'),
    output('entity', 'Entity', 'entity'),
  ],
  compile(ctx) {
    return ctx.emit('bridge:overlapPoint', [
      ctx.resolveInput('x'), ctx.resolveInput('y'),
      ctx.resolveInput('layer'),
    ], 'overlapResult');
  },
  execute(ctx) {
    const r = ctx.bridge.call('bridge:overlapPoint', ctx.getInput<number>('x'), ctx.getInput<number>('y'), ctx.getInput<number>('layer')) as { hit: boolean; entity: number } | null;
    ctx.setOutput('hit', r?.hit ?? false);
    ctx.setOutput('entity', r?.entity ?? -1);
  },
};

// ============================================================================
// BODY PROPERTIES
// ============================================================================

export const SetBodyType: NodeBehaviour = {
  id: 'physics.setBodyType',
  name: 'Set Body Type',
  category: 'Physics',
  color: '#4ecdc4',
  icon: 'Box',
  description: 'Changes the body type (0=Dynamic, 1=Kinematic, 2=Static).',
  keywords: ['dynamic', 'static', 'kinematic'],
  inputs: [
    flowIn(),
    input('entity', 'Entity', 'entity', 0),
    input('type', 'Body Type', 'i32', 0),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:setBodyType', [ctx.resolveInput('entity'), ctx.resolveInput('type')], 'void', true);
  },
  execute(ctx) {
    const eid = ctx.getInput<number>('entity') || ctx.entityId;
    ctx.bridge.call('bridge:setBodyType', eid, ctx.getInput<number>('type'));
  },
};

export const SetGravityScale: NodeBehaviour = {
  id: 'physics.setGravityScale',
  name: 'Set Gravity Scale',
  category: 'Physics',
  color: '#4ecdc4',
  icon: 'ArrowDown',
  description: 'Sets how much gravity affects this body.',
  keywords: ['weight', 'float', 'fall'],
  inputs: [
    flowIn(),
    input('entity', 'Entity', 'entity', 0),
    input('scale', 'Scale', 'f32', 1),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:setGravityScale', [ctx.resolveInput('entity'), ctx.resolveInput('scale')], 'void', true);
  },
  execute(ctx) {
    const eid = ctx.getInput<number>('entity') || ctx.entityId;
    ctx.bridge.call('bridge:setGravityScale', eid, ctx.getInput<number>('scale'));
  },
};

export const SetMass: NodeBehaviour = {
  id: 'physics.setMass',
  name: 'Set Mass',
  category: 'Physics',
  color: '#4ecdc4',
  icon: 'Weight',
  description: 'Sets the mass of a rigidbody.',
  keywords: ['weight', 'heavy', 'light'],
  inputs: [
    flowIn(),
    input('entity', 'Entity', 'entity', 0),
    input('mass', 'Mass', 'f32', 1),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:setMass', [ctx.resolveInput('entity'), ctx.resolveInput('mass')], 'void', true);
  },
  execute(ctx) {
    const eid = ctx.getInput<number>('entity') || ctx.entityId;
    ctx.bridge.call('bridge:setMass', eid, ctx.getInput<number>('mass'));
  },
};

export const GetMass: NodeBehaviour = {
  id: 'physics.getMass',
  name: 'Get Mass',
  category: 'Physics',
  isPure: true,
  color: '#4ecdc4',
  icon: 'Weight',
  description: 'Gets the mass of a rigidbody.',
  inputs: [
    input('entity', 'Entity', 'entity', 0),
  ],
  outputs: [
    output('mass', 'Mass', 'f32'),
  ],
  compile(ctx) {
    return ctx.emit('bridge:getMass', [ctx.resolveInput('entity')], 'f32');
  },
  execute(ctx) {
    const eid = ctx.getInput<number>('entity') || ctx.entityId;
    ctx.setOutput('mass', ctx.bridge.call('bridge:getMass', eid) as number ?? 1);
  },
};

export const SetFriction: NodeBehaviour = {
  id: 'physics.setFriction',
  name: 'Set Friction',
  category: 'Physics',
  color: '#4ecdc4',
  icon: 'Layers',
  description: 'Sets the friction coefficient of a collider.',
  keywords: ['slip', 'grip', 'slide'],
  inputs: [
    flowIn(),
    input('entity', 'Entity', 'entity', 0),
    input('friction', 'Friction', 'f32', 0.5),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:setFriction', [ctx.resolveInput('entity'), ctx.resolveInput('friction')], 'void', true);
  },
  execute(ctx) {
    const eid = ctx.getInput<number>('entity') || ctx.entityId;
    ctx.bridge.call('bridge:setFriction', eid, ctx.getInput<number>('friction'));
  },
};

export const SetBounciness: NodeBehaviour = {
  id: 'physics.setBounciness',
  name: 'Set Bounciness',
  category: 'Physics',
  color: '#4ecdc4',
  icon: 'Circle',
  description: 'Sets the restitution (bounciness) of a collider.',
  keywords: ['restitution', 'bounce', 'elastic'],
  inputs: [
    flowIn(),
    input('entity', 'Entity', 'entity', 0),
    input('bounce', 'Bounciness', 'f32', 0),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:setBounciness', [ctx.resolveInput('entity'), ctx.resolveInput('bounce')], 'void', true);
  },
  execute(ctx) {
    const eid = ctx.getInput<number>('entity') || ctx.entityId;
    ctx.bridge.call('bridge:setBounciness', eid, ctx.getInput<number>('bounce'));
  },
};

// ============================================================================
// CONSTRAINTS
// ============================================================================

export const FreezePosition: NodeBehaviour = {
  id: 'physics.freezePosition',
  name: 'Freeze Position',
  category: 'Physics',
  color: '#4ecdc4',
  icon: 'Lock',
  description: 'Locks position axes of a rigidbody.',
  keywords: ['lock', 'constrain', 'fix'],
  inputs: [
    flowIn(),
    input('entity', 'Entity', 'entity', 0),
    input('x', 'Freeze X', 'bool', false),
    input('y', 'Freeze Y', 'bool', false),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:freezePosition', [
      ctx.resolveInput('entity'),
      ctx.resolveInput('x'), ctx.resolveInput('y'),
    ], 'void', true);
  },
  execute(ctx) {
    const eid = ctx.getInput<number>('entity') || ctx.entityId;
    ctx.bridge.call('bridge:freezePosition', eid, ctx.getInput<boolean>('x'), ctx.getInput<boolean>('y'));
  },
};

export const FreezeRotation: NodeBehaviour = {
  id: 'physics.freezeRotation',
  name: 'Freeze Rotation',
  category: 'Physics',
  color: '#4ecdc4',
  icon: 'Lock',
  description: 'Locks rotation of a rigidbody.',
  keywords: ['lock', 'constrain', 'fix'],
  inputs: [
    flowIn(),
    input('entity', 'Entity', 'entity', 0),
    input('freeze', 'Freeze', 'bool', true),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:freezeRotation', [ctx.resolveInput('entity'), ctx.resolveInput('freeze')], 'void', true);
  },
  execute(ctx) {
    const eid = ctx.getInput<number>('entity') || ctx.entityId;
    ctx.bridge.call('bridge:freezeRotation', eid, ctx.getInput<boolean>('freeze'));
  },
};

// ============================================================================
// COLLISION LAYER
// ============================================================================

export const SetCollisionLayer: NodeBehaviour = {
  id: 'physics.setCollisionLayer',
  name: 'Set Collision Layer',
  category: 'Physics',
  color: '#4ecdc4',
  icon: 'Layers',
  description: 'Sets which layer this collider belongs to.',
  keywords: ['mask', 'filter', 'group'],
  inputs: [
    flowIn(),
    input('entity', 'Entity', 'entity', 0),
    input('layer', 'Layer', 'i32', 0),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:setCollisionLayer', [ctx.resolveInput('entity'), ctx.resolveInput('layer')], 'void', true);
  },
  execute(ctx) {
    const eid = ctx.getInput<number>('entity') || ctx.entityId;
    ctx.bridge.call('bridge:setCollisionLayer', eid, ctx.getInput<number>('layer'));
  },
};

export const SetCollisionMask: NodeBehaviour = {
  id: 'physics.setCollisionMask',
  name: 'Set Collision Mask',
  category: 'Physics',
  color: '#4ecdc4',
  icon: 'Filter',
  description: 'Sets which layers this collider can collide with.',
  keywords: ['filter', 'ignore', 'include'],
  inputs: [
    flowIn(),
    input('entity', 'Entity', 'entity', 0),
    input('mask', 'Mask', 'i32', -1),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:setCollisionMask', [ctx.resolveInput('entity'), ctx.resolveInput('mask')], 'void', true);
  },
  execute(ctx) {
    const eid = ctx.getInput<number>('entity') || ctx.entityId;
    ctx.bridge.call('bridge:setCollisionMask', eid, ctx.getInput<number>('mask'));
  },
};

// ============================================================================
// PHYSICS STATE
// ============================================================================

export const WakeUp: NodeBehaviour = {
  id: 'physics.wakeUp',
  name: 'Wake Up',
  category: 'Physics',
  color: '#4ecdc4',
  icon: 'Zap',
  description: 'Wakes a sleeping rigidbody.',
  keywords: ['activate', 'enable'],
  inputs: [
    flowIn(),
    input('entity', 'Entity', 'entity', 0),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:wakeUp', [ctx.resolveInput('entity')], 'void', true);
  },
  execute(ctx) {
    const eid = ctx.getInput<number>('entity') || ctx.entityId;
    ctx.bridge.call('bridge:wakeUp', eid);
  },
};

export const Sleep: NodeBehaviour = {
  id: 'physics.sleep',
  name: 'Sleep',
  category: 'Physics',
  color: '#4ecdc4',
  icon: 'Moon',
  description: 'Forces a rigidbody to sleep (optimization).',
  keywords: ['deactivate', 'disable'],
  inputs: [
    flowIn(),
    input('entity', 'Entity', 'entity', 0),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:sleep', [ctx.resolveInput('entity')], 'void', true);
  },
  execute(ctx) {
    const eid = ctx.getInput<number>('entity') || ctx.entityId;
    ctx.bridge.call('bridge:sleep', eid);
  },
};

export const IsSleeping: NodeBehaviour = {
  id: 'physics.isSleeping',
  name: 'Is Sleeping',
  category: 'Physics',
  isPure: true,
  color: '#4ecdc4',
  icon: 'Moon',
  description: 'Checks if a rigidbody is sleeping.',
  inputs: [
    input('entity', 'Entity', 'entity', 0),
  ],
  outputs: [
    output('sleeping', 'Is Sleeping', 'bool'),
  ],
  compile(ctx) {
    return ctx.emit('bridge:isSleeping', [ctx.resolveInput('entity')], 'bool');
  },
  execute(ctx) {
    const eid = ctx.getInput<number>('entity') || ctx.entityId;
    ctx.setOutput('sleeping', ctx.bridge.call('bridge:isSleeping', eid) as boolean ?? false);
  },
};

export const PHYSICS_ADVANCED_NODES: readonly NodeBehaviour[] = [
  SetAngularVelocity, GetAngularVelocity, AddTorque, AddForceAtPoint,
  OverlapCircle, OverlapBox, OverlapPoint,
  SetBodyType, SetGravityScale, SetMass, GetMass, SetFriction, SetBounciness,
  FreezePosition, FreezeRotation, SetCollisionLayer, SetCollisionMask,
  WakeUp, Sleep, IsSleeping,
];
